import React, { useRef, useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, IconButton, Chip, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import { useThemeContext } from '../../../Context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { DARKCOLORS, LIGHTCOLORS, ROUTES } from '../../../Utils/constants';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../../Components/Common/dateFormat';

function AdvertisementCard({ advertisement, locations }) {
    const navigate = useNavigate();
    const { mode } = useThemeContext();
    const { t } = useTranslation();

    const statusLabels = {
        Live: t('Advertisement.live'),
        Scheduled: t('Advertisement.scheduled'),
        Expired: t('Advertisement.expired'),
    };

    const chipColorLive = mode === 'dark' ? DARKCOLORS.Success500 : LIGHTCOLORS.Success500;
    const chipColorSchedule = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
    const chipColorDraft = mode === 'dark' ? DARKCOLORS.Warning500 : LIGHTCOLORS.Warning500;
    const btnborderColor = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
    const textborderColor = mode === 'dark' ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale30;
    const backgroundCard = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const badgecolor =  DARKCOLORS.BodyDarkBackground;

    const { name, startDate, endDate, vendingMachineIds, videoUrls, status } = advertisement;

    const videoRef = useRef(null); // Reference to the video element
    const [isPlaying, setIsPlaying] = useState(false); // Local state for play/pause
    const [isHovered, setIsHovered] = useState(false); // State to track hover

    const videoUrl = videoUrls && videoUrls.length > 0 ? videoUrls[0] : null;
    const dateRange = startDate && endDate ? `${formatDate(startDate)} to ${formatDate(endDate)}` : 'Select Dates';

    // Handle play/pause toggle
    const handlePlayPause = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
                setIsPlaying(false); // Stop playing
            } else {
                videoRef.current.play();
                setIsPlaying(true); // Start playing
            }
        }
    };

    useEffect(() => {
        // Pause video if the component unmounts
        return () => {
            if (videoRef.current) {
                videoRef.current.pause();
            }
        };
    }, []);

    // Navigate to the modal for editing
    const handleEditClick = () => {
        navigate(ROUTES.ADD_ADVERTISEMENT, { state: { mode: "update", advertisementId: advertisement.id } });
    };

    // Helper function to get the location names from the machine IDs
    const getLocationNames = () => {
        if (!locations || !vendingMachineIds || vendingMachineIds.length === 0) return 'Select location';

        const machineLocations = vendingMachineIds.map(id => {
            for (const city of locations) {
                const machine = city.machines.find(machine => machine.machineId === id);
                if (machine) return `${machine.location} (${city.city})`;
            }
            return null;
        });

        const uniqueLocations = [...new Set(machineLocations)].filter(Boolean);

        // Check if all vending machine locations are selected
        const allMachines = locations.flatMap(city => city.machines.map(machine => machine.machineId));
        const isAllLocationsSelected = vendingMachineIds.length === allMachines.length;

        return isAllLocationsSelected ? 'All locations' : uniqueLocations.join(', ');
    };

    return (
        <Card sx={{ marginBottom: 3, borderRadius: "10px", boxShadow: "none", background: btnborderColor }}>
            <Box
                sx={{
                    position: 'relative',
                    height: '220px',
                    overflow: 'hidden',
                    borderRadius: '8px 8px 0 0',
                    cursor: 'pointer'
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {videoUrl ? (
                    <video
                        ref={videoRef}
                        src={videoUrl}
                        title="Advertisement Video"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                        muted
                        loop
                    />
                ) : (
                    <Box
                        sx={{
                            backgroundColor: '#ccc',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <Typography variant="h6" color="white">
                            {t('Advertisement.NoVideoAvailable')}
                        </Typography>
                    </Box>
                )}
                {/* Play/Pause Button Overlay (Visible on Hover) */}
                {isHovered && videoUrl && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            background: 'rgba(0, 0, 0, 0.4)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            transition: 'opacity 0.3s',
                        }}
                        onClick={handlePlayPause}
                    >
                        {isPlaying ? (
                            <PauseCircleOutlineIcon sx={{ fontSize: 50, color: 'white' }} />
                        ) : (
                            <PlayCircleOutlineIcon sx={{ fontSize: 50, color: 'white' }} />
                        )}
                    </Box>
                )}
            </Box>
            <CardContent sx={{ p: 2, paddingBottom: "10px !important" }}>
                {/* Advertisement Name and Options */}
                {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <p className='machine-name-text' style={{ maxWidth: '200px' }}>{name || 'Untitled Advertisement'}</p>
                        {status && (
                            <Chip
                                label={status}
                                size="small"
                                sx={{
                                    backgroundColor:
                                        status === 'Live'
                                            ? chipColorLive
                                            : status === 'Scheduled'
                                                ? chipColorSchedule
                                                : chipColorDraft,
                                    color: 'white',
                                }}
                            />
                        )}
                    </Box>
                    <IconButton size="small" style={{ padding: "0px" }} onClick={handleEditClick}>
                        <MoreVertIcon style={{ marginRight: "-5px" }} />
                    </IconButton>
                </Box> */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    {/* Left side: name + chip */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: 1 }}>
                        {/* Constrain text truncation */}
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 1 }}>
                            <Tooltip title={name || 'Untitled Advertisement'}>
                                <p className="machine-name-text">{name || 'Untitled Advertisement'}</p>
                            </Tooltip>

                            {status && (
                                <Chip
                                    label={statusLabels[status] || status}
                                    size="small"
                                    sx={{
                                        backgroundColor:
                                            status === 'Live'
                                                ? chipColorLive
                                                : status === 'Scheduled'
                                                    ? chipColorSchedule
                                                    : chipColorDraft,
                                        color: badgecolor,
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Right side: icon */}
                    <IconButton size="small" style={{ padding: '0px' }} onClick={handleEditClick}>
                        <MoreVertIcon style={{ marginRight: '-5px' }} />
                    </IconButton>
                </Box>


                {/* Location and Date */}
                <Box sx={{
                    display: 'flex', alignItems: 'center',
                    marginBottom: "0.5rem", gap: "1px",
                    border: `0.5px solid ${textborderColor}`,
                    borderRadius: "6px", padding: "8px",
                }}>
                    <LocationOnIcon sx={{ fontSize: 16, color: 'gray', mr: 1 }} />
                    <Typography variant="body2" sx={{
                        maxWidth: '600px', // or whatever fits your layout
                        display: 'block',
                    }}
                        className='add-machine' color="textSecondary">
                        {getLocationNames()}
                    </Typography>
                </Box>
                <Box sx={{
                    display: 'flex', alignItems: 'center',
                    marginBottom: "0.5rem",
                    background: backgroundCard,
                    borderRadius: "6px", padding: "10px",
                }}>
                    <EventIcon sx={{ fontSize: 16, color: 'gray', mr: 1 }} />
                    <Typography variant="body2" color="textSecondary">
                        {dateRange}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

export default AdvertisementCard;
